import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import api from "../lib/api";

const KEY = "ai_theme";
const ThemeContext = createContext(null);

const systemDark = () =>
    window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

export function ThemeProvider({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const [theme, setThemeState] = useState(() => {
        const t = localStorage.getItem(KEY);
        return ["dark", "light", "system"].includes(t) ? t : "system";
    });
    const adoptedFor = useRef(null);

    // Adopt the saved profile preference once per login session
    useEffect(() => {
        if (user && user.id && user.theme_preference && adoptedFor.current !== user.id) {
            adoptedFor.current = user.id;
            if (user.theme_preference !== theme) {
                setThemeState(user.theme_preference);
                localStorage.setItem(KEY, user.theme_preference);
            }
        }
    }, [user, theme]);

    const resolved = (theme === "system" ? (systemDark() ? "dark" : "light") : theme);
    // The public landing stays cinematic dark regardless of preference
    const effective = location.pathname === "/" ? "dark" : resolved;

    useEffect(() => {
        const root = document.documentElement;
        const apply = () => {
            const r = theme === "system" ? (systemDark() ? "dark" : "light") : theme;
            const final = location.pathname === "/" ? "dark" : r;
            root.classList.remove("dark", "light");
            root.classList.add(final);
            root.style.colorScheme = final;
        };
        apply();
        if (theme === "system" && window.matchMedia) {
            const mq = window.matchMedia("(prefers-color-scheme: dark)");
            mq.addEventListener("change", apply);
            return () => mq.removeEventListener("change", apply);
        }
    }, [theme, location.pathname]);

    const setTheme = useCallback((t) => {
        setThemeState(t);
        localStorage.setItem(KEY, t);
        if (user && user.id) {
            api.patch("/account/profile", { theme: t }).catch(() => {});
        }
    }, [user]);

    return (
        <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme: effective }}>
            {children}
        </ThemeContext.Provider>
    );
}

export const useTheme = () => useContext(ThemeContext);
